/**
 * Row UI for a single RelationshipTree node.
 *
 * Layout: `[NodeTag] [ReferenceSelect | operation Text] [NotesPopover]
 * [Trophy | spacer] [More menu]`. Resolves the entity descriptor from
 * `config.entityConfig[entityOrGroupId]` to drive ReferenceSelect keying,
 * recipient-controls visibility, and operation-type rendering (no record
 * picker, label only). The tree persists a bare `entityRecordId`, so this row
 * subscribes to the record and wraps it into an `ArEntityReference` carrying
 * `cacheLabel`, which paints the name on first render and tracks renames.
 * Owns local `notesOpen` state and provides the opener
 * to the menu builder injected by the parent: `buildMenu({ openNotes })`
 * returns the final `MenuProps['items']` so the menu's "Edit Notes" item
 * directly opens this row's notes popover. The trophy slot is always
 * reserved as a fixed-width spacer when the node is not winning so row
 * widths stay stable without rendering a duplicate (transparent) icon that
 * assistive tech could pick up.
 *
 * The row carries the `tree.*` keybindings, whose handlers are derived
 * positionally from the built menu items by key prefix — never from a parallel
 * callback set — so the keyboard inherits every gate `buildNodeMenu` encodes and
 * a disabled item silently no-ops; `menuDisabled` withholds the whole set, so
 * the keyboard can never outrun the dropdown. The delete confirmation carries
 * its own gate, `deleteConfirmDisabled`: that popconfirm stays open after the
 * dropdown that spawned it closes, so it is the one menu action that can still
 * commit after the menu's own Delete item is gone — frozen for a run in flight,
 * or withdrawn because the tree gained a winning combination. Its Delete
 * button is focused through `focusDeleteConfirm`, never through the button's
 * own `autoFocus`: React's autoFocus cannot pass `preventScroll`, and
 * rc-trigger parks the popup at `top: -1000vh` until its align pass runs, so
 * that focus threw the whole page to the top the instant the confirmation
 * opened (SYD-2883, the rule `ViewVisibilityControl` records for SYD-2761).
 * The ref rides `okText` rather than `okButtonProps` because antd composes
 * that button as `_util/ActionButton`, which spreads the caller's props and
 * then overwrites `ref` with its own — a ref passed there is dropped with no
 * type error and no failing test. Two invariants hold it up: the Popconfirm
 * keeps `destroyOnHidden`, so the ref callback runs on EVERY open and not
 * only the first, and `okButtonProps` carries no `href`, which would make
 * antd render an anchor that `closest('button')` never finds.
 *
 * The chords are read in the CAPTURE phase, which is load-bearing:
 * `@rc-component/select` calls `preventDefault()` on every `Enter` keydown
 * regardless of modifiers, and `useKeybindings` stands down on a
 * `defaultPrevented` event — read on the bubble path, every ⌘Enter chord is dead
 * the moment focus sits in the row's record picker, i.e. always. Capture also
 * means no future descendant can eat a chord the row owns. Every chord still
 * carries `mod` because that picker's input passes through here and
 * `useKeybindings` has no editable-target guard.
 *
 * The set fires only for keydowns originating inside the row's own DOM subtree.
 * The notes popover and the delete Popconfirm are React descendants portaled to
 * `body`, so containment — not `notesOpen` — is what keeps a half-typed note's
 * ⌘⌫ from popping a delete confirmation, while leaving the row's own chords live
 * whenever that popover merely happens to be open.
 *
 * `tabIndex={-1}` makes the row click-focusable without becoming a tab stop:
 * clicking the tag or the row's padding would otherwise leave focus on `body`
 * and no chord would reach anything, while a tabbable row would inject a
 * non-editable container that is an ancestor of its own picker into the shared
 * form-field navigation list (every focusable selector there excludes
 * `tabindex="-1"`). `aria-keyshortcuts` rides the more-actions button — the
 * row's one always-tabbable control, and the trigger for the very menu the
 * chords mirror.
 */
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Popconfirm, Tooltip, Typography, theme } from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import Icon from '@/components/atoms/Icon';
import type { MenuProps } from 'antd';
import { ariaKeyshortcutsFor, useKeybindings } from '@/keyboard';
import type {
  ArEntityReference,
  NodeExtraInformation,
  RecipientEmailConfig,
  RelationshipTreeConfig,
  RelationshipTreeType,
} from '#types';
import FlexBox from '@/components/atoms/FlexBox';
import ReferenceSelect from '@/components/ArTable/components/ArCell/ReferenceSelect';
import { useEntityRecordStore } from '@/store';
import NodeTag from './NodeTag';
import NotesPopover from './NotesPopover';

type BuildMenuApi = {
  openNotes: () => void;
};

type NodeMenuAction = {
  key: string;
  disabled?: boolean;
  onClick?: () => void;
};

const TREE_ROW_SHORTCUT_IDS = [
  'tree.addChild1',
  'tree.addChild2',
  'tree.addSibling',
  'tree.duplicate',
  'tree.editNotes',
  'tree.deleteNode',
];

const TREE_ROW_KEYSHORTCUTS = TREE_ROW_SHORTCUT_IDS.map(ariaKeyshortcutsFor)
  .filter((value) => value.length > 0)
  .join(' ');

const menuHandlersByPrefix = (
  items: MenuProps['items'],
  prefix: string,
): (() => void)[] =>
  (items ?? [])
    .filter(
      (item): item is NodeMenuAction =>
        !!item &&
        'key' in item &&
        typeof item.key === 'string' &&
        item.key.startsWith(prefix),
    )
    .map((action) => () => {
      if (action.disabled) return;
      action.onClick?.();
    });

type NodeRowProps = {
  nodeKey: string;
  value?: string;
  onChange: (entityRecordId: string | undefined) => void;
  disabled?: boolean;
  treeType: RelationshipTreeType;
  entityOrGroupId: string;
  buildMenu?: (api: BuildMenuApi) => MenuProps['items'];
  menuDisabled?: boolean;
  isWinning?: boolean;
  extraInformation?: NodeExtraInformation;
  onExtraInfoChange?: (next: NodeExtraInformation) => void;
  recipientConfig?: RecipientEmailConfig;
  onRecipientConfigChange?: (next: RecipientEmailConfig) => void;
  config: RelationshipTreeConfig;
  deleteConfirmOpen?: boolean;
  deleteConfirmMessage?: string;
  deleteConfirmDisabled?: boolean;
  onDeleteConfirm?: () => void;
  onDeleteCancel?: () => void;
};

const NodeRow: FC<NodeRowProps> = ({
  nodeKey,
  value,
  onChange,
  disabled,
  treeType,
  entityOrGroupId,
  buildMenu,
  menuDisabled = false,
  isWinning,
  extraInformation,
  onExtraInfoChange,
  recipientConfig,
  onRecipientConfigChange,
  config,
  deleteConfirmOpen,
  deleteConfirmMessage,
  deleteConfirmDisabled = false,
  onDeleteConfirm,
  onDeleteCancel,
}) => {
  const [notesOpen, setNotesOpen] = useState(false);
  const { token } = theme.useToken();
  const moreActionsRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const focusDeleteConfirm = useCallback((node: HTMLSpanElement | null) => {
    node?.closest('button')?.focus({ preventScroll: true });
  }, []);

  const entityConfig = config.entityConfig[entityOrGroupId];
  const entityId = entityConfig?.entityId ?? entityOrGroupId;
  const entityGroupId =
    entityConfig?.kind === 'entityGroup' ? entityConfig.entityGroupId : undefined;
  const isOperationType = entityConfig?.isOperationType === true;
  const showRecipientControls =
    treeType === 'recipient' && Boolean(entityConfig?.defaultRecipientConfig);

  const menuItems = buildMenu?.({ openNotes: () => setNotesOpen(true) });

  const keyboardItems = menuDisabled ? undefined : menuItems;
  const addChildHandlers = menuHandlersByPrefix(keyboardItems, 'add-child:');
  const addSiblingHandlers = menuHandlersByPrefix(keyboardItems, 'add-sibling:');
  const { onKeyDown: chordKeyDown } = useKeybindings({
    'tree.addChild1': addChildHandlers[0],
    'tree.addChild2': addChildHandlers[1],
    'tree.addSibling': addSiblingHandlers[0],
    'tree.duplicate': menuHandlersByPrefix(keyboardItems, 'duplicate:')[0],
    'tree.editNotes': menuHandlersByPrefix(keyboardItems, 'edit-info:')[0],
    'tree.deleteNode': menuHandlersByPrefix(keyboardItems, 'delete:')[0],
  });

  const nodeIdentity = `${entityConfig?.abbreviation ?? entityOrGroupId}:${nodeKey}`;
  const moreActionsLabel = `more-actions ${nodeIdentity}`;
  const notesLabel = `notes-button ${nodeIdentity}`;

  const isRecordBound = !!value && !isOperationType;
  const recordStore = useEntityRecordStore(
    isRecordBound ? entityId : '',
    isRecordBound ? value : undefined,
  );
  const record = recordStore.entityRecord.data;

  /**
   * The stored value is a bare id, so ReferenceSelect has to resolve the name
   * itself. It can — a registry cache miss kicks off a background fetch that
   * re-renders the picker — but that path paints blank until the fetch lands,
   * and the registry bucket carries no Firestore listener, so a rename by
   * another client stays invisible until the bucket is torn down and reloaded.
   * `cacheLabel` from this row's live record subscription covers both gaps.
   */
  const referenceValue = useMemo<ArEntityReference | null>(() => {
    if (!value) return null;
    return {
      referenceType: 'entity',
      docId: value,
      ...(record?.name && { cacheLabel: record.name }),
    };
  }, [value, record?.name]);

  const resolvedEmail = useMemo(() => {
    if (!record) return null;
    const raw = (record as unknown as {
      fields?: { email?: { value?: unknown } };
    })?.fields?.email?.value;
    if (typeof raw === 'string') return raw;
    if (
      raw &&
      typeof raw === 'object' &&
      typeof (raw as { formatted?: unknown }).formatted === 'string'
    ) {
      return (raw as { formatted: string }).formatted;
    }
    return null;
  }, [record]);

  return (
    <FlexBox
      gap={token.marginXXS}
      justifyStart
      stretch
      style={{
        width: '100%',
        maxWidth: 500,
        backgroundColor: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadius,
        paddingLeft: token.paddingXS,
        paddingRight: token.paddingXXS,
        paddingTop: token.paddingXXS,
        paddingBottom: token.paddingXXS,
      }}
      aria-label={`relationship-tree-node-row ${nodeIdentity}`}
      tabIndex={-1}
      onKeyDownCapture={(e) => {
        if (!e.currentTarget.contains(e.target as Node)) return;
        chordKeyDown(e);
      }}
    >
      <NodeTag entityOrGroupId={entityOrGroupId} config={config} />
      {isOperationType ? (
        <FlexBox justifyStart stretch style={{ width: '100%' }}>
          <Typography.Text>
            {entityConfig?.label ?? entityOrGroupId}
          </Typography.Text>
        </FlexBox>
      ) : (
        <ReferenceSelect
          key={`${entityId}:${entityGroupId ?? ''}`}
          entityId={entityId}
          entityGroupId={entityGroupId}
          value={referenceValue}
          disableManualEntry
          onChange={(newValue) => {
            if (newValue && typeof newValue === 'object' && 'docId' in newValue) {
              onChange(newValue.docId as string);
              return;
            }
            onChange(undefined);
          }}
          disabled={disabled}
          style={{ flex: 1, minWidth: token.controlHeight * 6 }}
        />
      )}
      <NotesPopover
        extraInformation={extraInformation}
        onExtraInfoChange={onExtraInfoChange}
        disabled={disabled}
        open={notesOpen}
        onOpenChange={setNotesOpen}
        showRecipientControls={showRecipientControls}
        recipientConfig={recipientConfig}
        onRecipientConfigChange={onRecipientConfigChange}
        resolvedEmail={resolvedEmail}
        ariaLabel={notesLabel}
      />
      {isWinning ? (
        <Tooltip title={entityConfig?.winningLabel ?? 'Winning Combination'}>
          <TrophyOutlined
            aria-label={`winning-indicator ${nodeIdentity}`}
            style={{ color: token.gold }}
          />
        </Tooltip>
      ) : (
        <TrophyOutlined aria-hidden style={{ color: 'transparent' }} />
      )}
      {menuItems && menuItems.length > 0 && (
        <Popconfirm
          title="Confirm Deletion"
          description={deleteConfirmMessage}
          open={!!deleteConfirmOpen}
          okText={<span ref={focusDeleteConfirm}>Delete</span>}
          okButtonProps={{
            danger: true,
            disabled: deleteConfirmDisabled,
            'aria-label': `confirm-delete-node ${nodeIdentity}`,
          }}
          cancelButtonProps={{ 'aria-label': `cancel-delete-node ${nodeIdentity}` }}
          onConfirm={onDeleteConfirm}
          onCancel={() => {
            onDeleteCancel?.();
            moreActionsRef.current?.focus();
          }}
          destroyOnHidden
          placement="left"
        >
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            disabled={menuDisabled}
          >
            <Button
              ref={moreActionsRef}
              type="text"
              icon={<Icon.ChevronsDown />}
              disabled={menuDisabled}
              aria-label={moreActionsLabel}
              aria-keyshortcuts={menuDisabled ? undefined : TREE_ROW_KEYSHORTCUTS}
            />
          </Dropdown>
        </Popconfirm>
      )}
    </FlexBox>
  );
};

export default NodeRow;
